import { makeAutoObservable, runInAction } from 'mobx';
import { tagApi } from '@/services/tagApi';
import type {
  CreateTagRequest,
  GetTagRequest,
  ListTagsRequest,
  Tag,
  UpdateTagRequest,
} from '@/types';
import { TagType } from '@/types';

class TagStore {
  // 标签列表状态（用于标签管理页面）
  tags: Tag[] = [];
  currentType: TagType = TagType.TAG_TYPE_CATEGORY; // 默认显示分类标签
  keyword = '';
  loading = false;

  // 全局存储所有类型的 tags（用于筛选和表单）
  allCategoryTags: Tag[] = [];
  allPageTypeTags: Tag[] = [];
  allVisualStyleTags: Tag[] = [];
  allTagsLoading = false;
  allTagsInitialized = false;

  constructor() {
    makeAutoObservable(this);
  }

  // 初始化并加载所有 tags
  async initializeAllTags(force = false) {
    // 如果正在加载中，跳过
    if (this.allTagsLoading) {
      return;
    }
    // 如果已经初始化过且有数据，且不是强制刷新，跳过（避免重复请求）
    if (
      !force &&
      this.allTagsInitialized &&
      this.allCategoryTags.length > 0 &&
      this.allPageTypeTags.length > 0 &&
      this.allVisualStyleTags.length > 0
    ) {
      return;
    }

    this.allTagsLoading = true;
    try {
      const [categories, pageTypes, visualStyles] = await Promise.all([
        tagApi.listTags({
          type: TagType.TAG_TYPE_CATEGORY,
        }),
        tagApi.listTags({
          type: TagType.TAG_TYPE_PAGE_TYPE,
        }),
        tagApi.listTags({
          type: TagType.TAG_TYPE_VISUAL_STYLE,
        }),
      ]);
      runInAction(() => {
        this.allCategoryTags = categories.tags;
        this.allPageTypeTags = pageTypes.tags;
        this.allVisualStyleTags = visualStyles.tags;
        this.allTagsInitialized = true;
        this.allTagsLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.allTagsLoading = false;
      });
      throw error;
    }
  }

  // 刷新特定类型的 tags
  async refreshTagsByType(type: TagType): Promise<Tag[]> {
    try {
      const response = await tagApi.listTags({
        type,
      });
      runInAction(() => {
        switch (type) {
          case TagType.TAG_TYPE_CATEGORY:
            this.allCategoryTags = response.tags;
            break;
          case TagType.TAG_TYPE_PAGE_TYPE:
            this.allPageTypeTags = response.tags;
            break;
          case TagType.TAG_TYPE_VISUAL_STYLE:
            this.allVisualStyleTags = response.tags;
            break;
        }
      });
      return response.tags;
    } catch (error) {
      console.error(`刷新 ${type} tags 失败:`, error);
      throw error;
    }
  }

  // 获取分类 tags
  get categoryTags() {
    return this.allCategoryTags;
  }

  // 获取页面类型 tags
  get pageTypeTags() {
    return this.allPageTypeTags;
  }

  // 获取视觉风格 tags
  get visualStyleTags() {
    return this.allVisualStyleTags;
  }

  // 获取标签列表
  async fetchTags(type?: TagType) {
    this.loading = true;
    const tagType = type ?? this.currentType;
    try {
      const request: ListTagsRequest = {
        type: tagType,
        keyword: this.keyword || undefined,
      };
      const response = await tagApi.listTags(request);
      runInAction(() => {
        this.tags = response.tags;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  // 获取单个标签
  async fetchTag(
    type: TagType,
    identifier: { id?: string; key?: string },
  ): Promise<Tag> {
    this.loading = true;
    try {
      let request: GetTagRequest;
      if (identifier.id) {
        request = { type, id: identifier.id };
      } else if (identifier.key) {
        request = { type, key: identifier.key };
      } else {
        throw new Error('Either id or key must be provided');
      }
      const response = await tagApi.getTag(request);
      runInAction(() => {
        this.loading = false;
      });
      return response.tag;
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  // 创建标签
  async createTag(data: CreateTagRequest) {
    this.loading = true;
    try {
      const response = await tagApi.createTag(data);
      // 创建后刷新全局 tags
      const refreshedTags = await this.refreshTagsByType(data.type);
      // 如果当前显示的就是这个类型且没有关键词搜索，复用刷新结果
      if (this.currentType === data.type && !this.keyword) {
        runInAction(() => {
          this.tags = refreshedTags;
        });
      } else if (this.currentType === data.type) {
        // 有关键词搜索，需要重新获取
        await this.fetchTags(data.type);
      }
      return response.tag;
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  // 更新标签
  async updateTag(data: UpdateTagRequest) {
    this.loading = true;
    try {
      const response = await tagApi.updateTag(data);
      // 更新后刷新全局 tags
      const refreshedTags = await this.refreshTagsByType(data.type);
      // 如果当前显示的就是这个类型且没有关键词搜索，复用刷新结果
      if (this.currentType === data.type && !this.keyword) {
        runInAction(() => {
          this.tags = refreshedTags;
        });
      } else if (this.currentType === data.type) {
        // 有关键词搜索，需要重新获取
        await this.fetchTags(data.type);
      }
      return response.tag;
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  // 删除标签
  async deleteTag(type: TagType, id: string) {
    this.loading = true;
    try {
      await tagApi.deleteTag({ type, id });
      // 删除后刷新全局 tags
      const refreshedTags = await this.refreshTagsByType(type);
      // 如果当前显示的就是这个类型且没有关键词搜索，复用刷新结果
      if (this.currentType === type && !this.keyword) {
        runInAction(() => {
          this.tags = refreshedTags;
        });
      } else if (this.currentType === type) {
        // 有关键词搜索，需要重新获取
        await this.fetchTags(type);
      }
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  // 设置标签类型
  setType(type: TagType) {
    this.currentType = type;
  }

  // 设置搜索关键词
  setKeyword(keyword: string) {
    this.keyword = keyword;
  }
}

export const tagStore = new TagStore();
